import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useAuth } from '../../../hooks/useAuth';

const BMC_BLOCKS = [
  {
    id: 'key_partners',
    label: 'Key Partners',
    gridArea: 'kp',
    hint: 'Who are our key partners and suppliers?',
  },
  {
    id: 'key_activities',
    label: 'Key Activities',
    gridArea: 'ka',
    hint: 'What key activities does our value proposition require?',
  },
  {
    id: 'key_resources',
    label: 'Key Resources',
    gridArea: 'kr',
    hint: 'What key resources does our value proposition require?',
  },
  {
    id: 'value_propositions',
    label: 'Value Propositions',
    gridArea: 'vp',
    hint: 'What value do we deliver to the customer?',
  },
  {
    id: 'customer_relationships',
    label: 'Customer Relationships',
    gridArea: 'cr',
    hint: 'What type of relationship does each segment expect?',
  },
  {
    id: 'channels',
    label: 'Channels',
    gridArea: 'ch',
    hint: 'Through which channels do customers want to be reached?',
  },
  {
    id: 'customer_segments',
    label: 'Customer Segments',
    gridArea: 'cs',
    hint: 'For whom are we creating value?',
  },
  {
    id: 'cost_structure',
    label: 'Cost Structure',
    gridArea: 'cost',
    hint: 'What are the most important costs in our business model?',
  },
  {
    id: 'revenue_streams',
    label: 'Revenue Streams',
    gridArea: 'rev',
    hint: 'For what value are customers willing to pay?',
  },
];

const INITIAL_STATE = Object.fromEntries(BMC_BLOCKS.map((b) => [b.id, '']));

const gridStyle = {
  display: 'grid',
  gridTemplateAreas: `
    "kp ka vp cr cs"
    "kp kr vp ch cs"
    "cost cost cost rev rev"
  `,
  gridTemplateColumns: '1fr 1fr 1.2fr 1fr 1fr',
  gridTemplateRows: '1fr 1fr 0.7fr',
  gap: '6px',
  minHeight: '500px',
  width: '100%',
};

export default function BMCFramework({ dealId }) {
  const { user, userProfile } = useAuth();
  const [data, setData] = useState(INITIAL_STATE);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db, 'deals', dealId, 'frameworks', 'business_model_canvas'))
      .then((snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setData({
            key_partners: d.key_partners || '',
            key_activities: d.key_activities || '',
            key_resources: d.key_resources || '',
            value_propositions: d.value_propositions || '',
            customer_relationships: d.customer_relationships || '',
            channels: d.channels || '',
            customer_segments: d.customer_segments || '',
            cost_structure: d.cost_structure || '',
            revenue_streams: d.revenue_streams || '',
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dealId]);

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    try {
      await setDoc(doc(db, 'deals', dealId, 'frameworks', 'business_model_canvas'), {
        completed_by: user?.uid || '',
        completed_by_name: userProfile?.full_name || userProfile?.display_name || '',
        completed_at: new Date().toISOString(),
        ...data,
      });
      setSaveMsg('Saved successfully');
    } catch (err) {
      setSaveMsg('Save failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)', fontSize: 13 }}>Loading...</div>;
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
        Complete the Business Model Canvas for this company. Each block auto-saves when you click Save Canvas.
      </p>

      <div style={{ overflowX: 'auto', marginBottom: 20 }}>
        <div style={gridStyle}>
          {BMC_BLOCKS.map((block) => (
            <div
              key={block.id}
              style={{
                gridArea: block.gridArea,
                border: '1px solid #e8e8e0',
                borderRadius: '6px',
                background: '#fff',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <div style={{
                background: '#1e3a5f',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 600,
                padding: '6px 10px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                flexShrink: 0,
              }}>
                {block.label}
              </div>
              <textarea
                value={data[block.id] || ''}
                onChange={(e) => setData((prev) => ({ ...prev, [block.id]: e.target.value }))}
                placeholder={block.hint}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  padding: '8px 10px',
                  fontSize: '12px',
                  fontFamily: 'inherit',
                  lineHeight: 1.5,
                  color: '#1a1a1a',
                  background: 'transparent',
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: '1px solid var(--color-accent)',
            background: 'var(--color-accent)',
            color: '#fff',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Canvas'}
        </button>
        {saveMsg && (
          <span style={{
            fontSize: 13,
            color: saveMsg.includes('failed') ? 'var(--color-error)' : 'var(--color-success)',
          }}>
            {saveMsg}
          </span>
        )}
      </div>
    </div>
  );
}
